import { bodyHealthId, bodyHealthTimestampCompare, validBodyHealthTimestamp } from '../../../supabase/functions/_shared/bodyHealthActionDomain';
import type { BodyHealthRecordGuardInfo } from '@/lib/bodyHealthRecordGuards';

export interface HealthMetricRow extends Partial<BodyHealthRecordGuardInfo> {
  id: string;
  user_id: string;
  metric_type: string;
  value: number | null;
  unit: string | null;
  recorded_at: string;
  source: string;
  external_id: string | null;
  metadata: Record<string, unknown> | null;
  ended_at?: string | null;
  created_at?: string | null;
}
export interface HealthHistoryWindow { from: string; to: string }
export interface HealthHistoryQuery extends PromiseLike<{ data: unknown; error: unknown }> {
  select(columns: string): HealthHistoryQuery;
  eq(column: string, value: string): HealthHistoryQuery;
  gte(column: string, value: string): HealthHistoryQuery;
  lte(column: string, value: string): HealthHistoryQuery;
  order(column: string, options: { ascending: boolean }): HealthHistoryQuery;
  limit(count: number): HealthHistoryQuery;
  or(filters: string): HealthHistoryQuery;
  abortSignal(signal: AbortSignal): HealthHistoryQuery;
  setHeader(name: string, value: string): HealthHistoryQuery;
}
/** Structural seam: the reader neither imports nor selects a global client. */
// The SDK select return has deeply recursive database generics. Erase that
// return only at this boundary; the reader uses the explicit query contract.
export interface HealthHistoryClient { from(table: 'health_metrics'): { select(columns: string): unknown } }
export interface HealthHistoryOptions {
  client: HealthHistoryClient;
  ownerId: string;
  bearer: string;
  signal: AbortSignal;
  assertCurrent: () => void;
  now?: number;
}
const HISTORY_DAYS = 90;
const PAGE_SIZE = 1000;
const PAGE_BUDGET = 20;
const COLUMNS = 'id,user_id,metric_type,value,unit,recorded_at,source,external_id,metadata,created_at,ended_at';
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const fail = (reason: string): never => { throw new Error('Health history ' + reason); };
function assertActive(options: HealthHistoryOptions): void {
  options.assertCurrent();
  if (options.signal.aborted) throw new DOMException('Health history aborted', 'AbortError');
}
function confirmedRow(value: unknown, ownerId: string, window: HealthHistoryWindow): HealthMetricRow {
  if (!object(value)) return fail('record not confirmed');
  const id = bodyHealthId(value.id), owner = bodyHealthId(value.user_id);
  if (owner !== ownerId || typeof value.metric_type !== 'string' || typeof value.source !== 'string'
    || value.value !== null && (typeof value.value !== 'number' || !Number.isFinite(value.value))
    || value.unit !== null && typeof value.unit !== 'string'
    || value.external_id !== null && typeof value.external_id !== 'string'
    || value.metadata !== null && !object(value.metadata)
    || !validBodyHealthTimestamp(value.recorded_at)
    || bodyHealthTimestampCompare(value.recorded_at, window.from) < 0 || bodyHealthTimestampCompare(value.recorded_at, window.to) > 0
    || value.created_at !== null && (!validBodyHealthTimestamp(value.created_at) || bodyHealthTimestampCompare(value.created_at, window.to) > 0)
    || value.ended_at !== null && !validBodyHealthTimestamp(value.ended_at)) return fail('owner, bounds or record not confirmed');
  return { ...value, id, user_id: owner } as unknown as HealthMetricRow;
}
function strictlyAfter(previous: HealthMetricRow, next: HealthMetricRow): boolean {
  const order = bodyHealthTimestampCompare(previous.recorded_at, next.recorded_at);
  return order > 0 || order === 0 && previous.id > next.id;
}

/**
 * Inclusive 90-day recorded-at window, fixed once per read. Keyset pages preserve
 * PostgreSQL microseconds; changing the order or repeating an ID aborts the read.
 * This is not a transactional snapshot. Edits/deletions during the read remain
 * possible. Legacy created_at=null rows are retained and cannot prove a creation
 * cutoff; non-null creation instants must be at or before the fixed upper bound.
 * Only an empty page proves exhaustion, even when the API caps a page below
 * the requested limit. The request budget never returns a partial result.
 */
export async function readHealthMetricHistory(options: HealthHistoryOptions): Promise<{ rows: HealthMetricRow[]; window: HealthHistoryWindow }> {
  assertActive(options);
  const owner = bodyHealthId(options.ownerId);
  if (typeof options.bearer !== 'string' || !options.bearer || Array.from(options.bearer).some(character => /\s/.test(character) || character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return fail('bearer invalid');
  const now = options.now ?? Date.now(), lower = now - HISTORY_DAYS * 86_400_000;
  if (!Number.isFinite(now) || !Number.isFinite(lower)) return fail('clock invalid');
  let window: HealthHistoryWindow;
  try { window = { from: new Date(lower).toISOString(), to: new Date(now).toISOString() }; } catch { return fail('clock invalid'); }
  if (!validBodyHealthTimestamp(window.from) || !validBodyHealthTimestamp(window.to)) return fail('clock invalid');
  const rows: HealthMetricRow[] = [], seen = new Set<string>();
  let cursor: HealthMetricRow | null = null;
  for (let pageNumber = 0; pageNumber < PAGE_BUDGET; pageNumber++) {
    assertActive(options);
    let query = (options.client.from('health_metrics').select(COLUMNS) as HealthHistoryQuery)
      .eq('user_id', owner).gte('recorded_at', window.from).lte('recorded_at', window.to)
      .or('created_at.is.null,created_at.lte.' + window.to)
      .order('recorded_at', { ascending: false }).order('id', { ascending: false }).limit(PAGE_SIZE)
      .abortSignal(options.signal).setHeader('Authorization', 'Bearer ' + options.bearer);
    if (cursor) query = query.or('recorded_at.lt.' + cursor.recorded_at + ',and(recorded_at.eq.' + cursor.recorded_at + ',id.lt.' + cursor.id + ')');
    const { data, error } = await query;
    assertActive(options);
    if (error) throw error;
    if (!Array.isArray(data) || data.length > PAGE_SIZE) return fail('page not confirmed');
    for (const raw of data) {
      const record = confirmedRow(raw, owner, window);
      if (seen.has(record.id) || cursor !== null && !strictlyAfter(cursor, record)) return fail('duplicate or order not confirmed');
      seen.add(record.id); rows.push(record); cursor = record;
    }
    if (data.length === 0) { assertActive(options); return { rows, window }; }
  }
  return fail('page budget exceeded');
}
